import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import PickTypeIndicator from 'ui-component/shared/PickTypeIndicator';
import { pickTypeMap } from 'types/inventory';
import { useWarehouseKitRequest } from 'contexts/WarehouseKitRequestContext';
import { useOrgAndTeamContext } from 'views/warehouse/OrgAndTeamContext';
import { MoveWarningData } from 'views/warehouse/MoveWarningContext/types';
import {
  requestTypeToPurpose,
  KIT_REQUEST_PURPOSES,
} from 'views/warehouse/utils';
import { StockLocationPurpose } from 'types/clientV2/stockLocations';

type StockLotRequestCardProps = {
  warningData: MoveWarningData;
};

export const StockLotRequestCard: FC<StockLotRequestCardProps> = ({
  warningData,
}) => {
  const theme = useTheme();
  const { kitRequests } = useWarehouseKitRequest();
  const { getOrgOrTeamDisplay } = useOrgAndTeamContext();

  return (
    <Box
      sx={{
        width: '300px',
        height: '105px',
        border: '1px solid',
        borderColor: theme.palette.grey[500],
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <PickTypeIndicator
          pickType={
            warningData.handlingRequest?.requestType &&
            warningData.handlingRequest.requestType in pickTypeMap
              ? pickTypeMap[warningData.handlingRequest.requestType]
              : warningData.handlingRequest?.requestType || ''
          }
          size="large"
        />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {warningData.stockLot.lotId}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {warningData.handlingRequest?.requestType &&
          requestTypeToPurpose[warningData.handlingRequest.requestType] &&
          (() => {
            const purpose =
              requestTypeToPurpose[warningData.handlingRequest.requestType];

            if (!purpose) {
              return (
                <Typography variant="body2" color="text.secondary">
                  Handling Request Required
                </Typography>
              );
            }

            // Kit request purposes - show kit request and org
            if (KIT_REQUEST_PURPOSES.includes(purpose)) {
              return (
                <>
                  {warningData.handlingRequest?.kitRequestIdViaLot && (
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    >
                      Kit:{' '}
                      {kitRequests.find(
                        (kr) =>
                          kr.id ===
                          warningData.handlingRequest?.kitRequestIdViaLot
                      )?.kitCode ||
                        warningData.handlingRequest.kitRequestIdViaLot}
                    </Typography>
                  )}
                </>
              );
            }

            // Receiving purpose - show shipment if available
            if (purpose === StockLocationPurpose.RECEIVING) {
              return (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 500 }}
                >
                  Receiving
                </Typography>
              );
            }

            // Putting purpose - show org/team
            if (purpose === StockLocationPurpose.PUTTING) {
              return (
                <>
                  {warningData.handlingRequest?.org && (
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    >
                      {getOrgOrTeamDisplay(
                        warningData.handlingRequest.org,
                        null
                      )}
                    </Typography>
                  )}
                </>
              );
            }

            // Default - show org if available
            return (
              <>
                {warningData.handlingRequest?.org && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ fontWeight: 500 }}
                  >
                    {getOrgOrTeamDisplay(warningData.handlingRequest.org, null)}
                  </Typography>
                )}
              </>
            );
          })()}
        {(!warningData.handlingRequest?.requestType ||
          !requestTypeToPurpose[warningData.handlingRequest.requestType]) && (
          <Typography variant="body2" color="text.secondary">
            Handling Request Required
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
